import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuth } from '@/contexts/AuthContext';
import { registerApnsToken } from '@/api/pushNotifications';
import { getStoredApnsToken } from '@/lib/apnsTokenStore';
import { getPlatform, openIosSettings } from '@/lib/platform';
import {
  getPushPermissionBannerMessage,
  normalizePushPermissionReceive,
  PUSH_MAIN_SHELL_PROMPTED_KEY,
  PUSH_SHEET_DISMISSED_KEY,
  shouldShowPushOptInSheet,
  type PushOptInEntryPoint,
  type PushPermissionReceive,
} from '@/lib/pushOptInTypes';
import PushPermissionPrompt from '@/components/PushNotifications/PushPermissionPrompt';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_PUSH_PERMISSION_DENIED,
  EVENT_PUSH_PERMISSION_GRANTED,
  EVENT_PUSH_PERMISSION_PROMPT_SHOWN,
  EVENT_PUSH_TOKEN_REGISTERED,
} from '@/lib/analyticsEvents';
import { debug, error } from '@/utils/logger';

interface PushOptInContextValue {
  permissionReceive: PushPermissionReceive | null;
  pushNotificationsAllowed: boolean;
  pushBannerMessage: string;
  maybeRequestOptIn: (options: { entryPoint: PushOptInEntryPoint }) => Promise<void>;
  requestPushPermissionFromBanner: () => Promise<void>;
  refreshPushPermissionStatus: () => Promise<void>;
}

const PushOptInContext = createContext<PushOptInContextValue | undefined>(undefined);

async function readPushPermissionReceive(): Promise<PushPermissionReceive | null> {
  if (getPlatform() !== 'ios') return null;
  try {
    const status = await PushNotifications.checkPermissions();
    return normalizePushPermissionReceive(status.receive);
  } catch (err) {
    error('[PushOptIn] checkPermissions failed', err);
    return null;
  }
}

async function waitForStoredApnsToken(timeoutMs = 3000): Promise<string | null> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const token = getStoredApnsToken();
    if (token) return token;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return getStoredApnsToken();
}

async function requestPushPermissionAndRegister(): Promise<PushPermissionReceive> {
  const requested = await PushNotifications.requestPermissions();
  if (requested.receive !== 'granted') {
    return normalizePushPermissionReceive(requested.receive);
  }

  await PushNotifications.register();
  const token = await waitForStoredApnsToken();
  if (token) {
    await registerApnsToken(token);
    WebAnalytics.track(EVENT_PUSH_TOKEN_REGISTERED);
  }
  return 'granted';
}

export const PushOptInProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasOnboarded } = useAuth();
  const [permissionReceive, setPermissionReceive] = useState<PushPermissionReceive | null>(null);
  const [promptVisible, setPromptVisible] = useState(false);
  const [promptEntryPoint, setPromptEntryPoint] = useState<PushOptInEntryPoint>('onboarding');
  const [isEnabling, setIsEnabling] = useState(false);
  const promptVisibleRef = useRef(false);
  const prevAuthenticatedRef = useRef(false);

  promptVisibleRef.current = promptVisible;

  const refreshPushPermissionStatus = useCallback(async () => {
    const receive = await readPushPermissionReceive();
    setPermissionReceive(receive);
  }, []);

  useEffect(() => {
    void refreshPushPermissionStatus();
  }, [refreshPushPermissionStatus]);

  const dismissPrompt = useCallback(() => {
    setPromptVisible(false);
    if (getPlatform() !== 'ios') return;
    void Preferences.set({ key: PUSH_SHEET_DISMISSED_KEY, value: 'true' }).catch((err) => {
      error('[PushOptIn] Failed to persist sheet dismiss', err);
    });
  }, []);

  const runEnableFlow = useCallback(
    async (entryPoint: PushOptInEntryPoint): Promise<void> => {
      if (getPlatform() !== 'ios') return;

      setIsEnabling(true);
      try {
        const result = await requestPushPermissionAndRegister();
        setPermissionReceive(result);

        if (result === 'granted') {
          WebAnalytics.track(EVENT_PUSH_PERMISSION_GRANTED, { entry_point: entryPoint });
          setPromptVisible(false);
        } else {
          WebAnalytics.track(EVENT_PUSH_PERMISSION_DENIED, { entry_point: entryPoint });
        }
      } catch (err) {
        error('[PushOptIn] Enable flow failed', err);
        WebAnalytics.track(EVENT_PUSH_PERMISSION_DENIED, { entry_point: entryPoint });
      } finally {
        setIsEnabling(false);
      }
    },
    []
  );

  const maybeRequestOptIn = useCallback(
    async ({ entryPoint }: { entryPoint: PushOptInEntryPoint }) => {
      if (getPlatform() !== 'ios') return;

      const receive = (permissionReceive ?? (await readPushPermissionReceive())) as PushPermissionReceive | null;
      if (receive) {
        setPermissionReceive(receive);
      }

      if (!receive || receive === 'granted') return;
      if (receive === 'denied') return;

      if (entryPoint === 'main_shell') {
        const { value } = await Preferences.get({ key: PUSH_MAIN_SHELL_PROMPTED_KEY });
        if (value === 'true') return;
        await Preferences.set({ key: PUSH_MAIN_SHELL_PROMPTED_KEY, value: 'true' });
      }

      const { value: sheetDismissed } = await Preferences.get({ key: PUSH_SHEET_DISMISSED_KEY });
      if (!shouldShowPushOptInSheet(receive, promptVisibleRef.current, sheetDismissed === 'true')) {
        return;
      }

      setPromptEntryPoint(entryPoint);
      setPromptVisible(true);
      WebAnalytics.track(EVENT_PUSH_PERMISSION_PROMPT_SHOWN, { entry_point: entryPoint });
      debug('[PushOptIn] Prompt shown', { entryPoint });
    },
    [permissionReceive]
  );

  const requestPushPermissionFromBanner = useCallback(async () => {
    if (getPlatform() !== 'ios') return;

    const receive = (permissionReceive ?? (await readPushPermissionReceive())) as PushPermissionReceive | null;
    if (receive) {
      setPermissionReceive(receive);
    }

    if (receive === 'denied') {
      try {
        openIosSettings();
      } catch (err) {
        error('[PushOptIn] Failed to open iOS Settings', err);
      }
      return;
    }

    await runEnableFlow('notifications_banner');
  }, [permissionReceive, runEnableFlow]);

  useEffect(() => {
    const justAuthenticated = isAuthenticated && !prevAuthenticatedRef.current;
    prevAuthenticatedRef.current = isAuthenticated;
    if (!justAuthenticated || !hasOnboarded) return;
    void maybeRequestOptIn({ entryPoint: 'login' });
  }, [hasOnboarded, isAuthenticated, maybeRequestOptIn]);

  const pushNotificationsAllowed = permissionReceive === 'granted';
  const pushBannerMessage = permissionReceive
    ? getPushPermissionBannerMessage(permissionReceive)
    : '';

  const value = useMemo(
    () => ({
      permissionReceive,
      pushNotificationsAllowed,
      pushBannerMessage,
      maybeRequestOptIn,
      requestPushPermissionFromBanner,
      refreshPushPermissionStatus,
    }),
    [
      maybeRequestOptIn,
      permissionReceive,
      pushBannerMessage,
      pushNotificationsAllowed,
      refreshPushPermissionStatus,
      requestPushPermissionFromBanner,
    ]
  );

  return (
    <PushOptInContext.Provider value={value}>
      {children}
      <PushPermissionPrompt
        isOpen={promptVisible}
        entryPoint={promptEntryPoint}
        onClose={dismissPrompt}
        onEnable={() => void runEnableFlow(promptEntryPoint)}
        isEnabling={isEnabling}
      />
    </PushOptInContext.Provider>
  );
};

export const usePushOptIn = (): PushOptInContextValue => {
  const context = useContext(PushOptInContext);
  if (!context) {
    throw new Error('usePushOptIn must be used within a PushOptInProvider');
  }
  return context;
};

export async function markMainShellPushOptInAttempted(): Promise<boolean> {
  if (getPlatform() !== 'ios') return false;
  const { value } = await Preferences.get({ key: PUSH_MAIN_SHELL_PROMPTED_KEY });
  if (value === 'true') return false;
  await Preferences.set({ key: PUSH_MAIN_SHELL_PROMPTED_KEY, value: 'true' });
  return true;
}
